/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: connections entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the connections table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the connections API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, user_id, connected_user_id, status, connection_method, is_favorite, last_contact_sync, created_at, updated_at]
 * Searchable fields: []
 * Filterable fields: [id, user_id, connected_user_id]
 */

import { httpClient } from '../http-client.js';
import { globalSearchConfig, type UnifiedQueryOptions } from '@root-config/search/global-search-config';
import { matchesAllTerms } from '@root-config/search/search-utils';
import type {
  ConnectionsRow,
  ConnectionsInsert,
  ConnectionsUpdate
} from '../../types/generated_schemas/generated-connections-schema.js';

export interface ConnectionsQueryOptions extends UnifiedQueryOptions {}

export interface ConnectionsResponse {
  data: ConnectionsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for connections table operations
 *
 * @description Provides type-safe access to connections API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class ConnectionsClient {
  private readonly basePath = '/connections';

  /**
   * Get all connections records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of connections records
   */
  async getAll(options?: ConnectionsQueryOptions): Promise<ConnectionsRow[]> {
    const params = new URLSearchParams();
    
    // Numeric params are compared against null, not tested for truthiness: offset: 0 is a
    // legitimate value (the first page) and a truthiness check silently dropped it.
    // The server reads these exact names — see buildOrderClause/buildPaginationClause in
    // scripts/generate-server-side-routes/generate-routes-utils/supabase-query-builder.ts.
    if (options?.limit != null) params.set('limit', options.limit.toString());
    if (options?.offset != null) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<ConnectionsResponse>(url);
    return response.data;
  }

  /**
   * Get a single connections record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the connections record
   */
  async getById(id: string): Promise<ConnectionsRow> {
    const response = await httpClient.get<{
      data: ConnectionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new connections record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created connections record
   */
  async create(data: ConnectionsInsert): Promise<ConnectionsRow> {
    const response = await httpClient.post<{
      data: ConnectionsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing connections record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated connections record
   */
  async update(id: string, data: ConnectionsUpdate): Promise<ConnectionsRow> {
    const response = await httpClient.put<{
      data: ConnectionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a connections record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple connections records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created connections records
   */
  async createMany(data: ConnectionsInsert[]): Promise<ConnectionsRow[]> {
    const response = await httpClient.post<{
      data: ConnectionsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple connections records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search connections with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    query: string,
    columns?: string[],
    options?: ConnectionsQueryOptions
  ): Promise<ConnectionsResponse> {
    throw new Error('Search operations are not available for connections - no searchable fields configured');
  }

  /**
   * Get count of connections records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a connections record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'connections',
      publicFields: ['id', 'user_id', 'connected_user_id', 'status', 'connection_method', 'is_favorite', 'last_contact_sync', 'created_at', 'updated_at'],
      searchableFields: [],
      filterableFields: ['id', 'user_id', 'connected_user_id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['id', 'user_id', 'connected_user_id', 'status', 'created_at', 'updated_at'],
    };
  }
}

// Export singleton instance for convenience
export const connectionsClient = new ConnectionsClient();
